import { C, SERIF, mono } from '../../theme';
import { Icon } from '../../icons';
import { Pill } from '../../components/Pill';
import { Loading, Empty } from '../../components/States';
import { api } from '../../api';
import type { Overview, TenancyLedger, TenancyRow } from '../../api';
import { useAsync } from '../../useAsync';
import { inr, inrShort, fmtPeriod, currentPeriod } from '../../format';
import { unitDesc, rowPill } from '../helpers';

interface LedgerPortfolio {
  rows: Array<{ t: TenancyRow; outstanding: number }>;
  overview: Overview | null;
}

/**
 * Portfolio ledger — every tenancy in one place with its current-month status
 * and full outstanding balance, plus the property's expected / collected /
 * overdue totals for the month. Rows deep-link into the tenancy detail.
 */
export function LedgerPage({
  propertyId,
  propertyName,
  onOpen,
}: {
  propertyId?: string;
  propertyName?: string;
  onOpen: (tenancyId: string) => void;
}) {
  const { data, loading, error, reload } = useAsync<LedgerPortfolio>(async () => {
    const tenancies = await api.tenancies();
    const ledgers = await Promise.all(
      tenancies.map((t) => api.ledger(t.id).catch(() => null as TenancyLedger | null)),
    );
    const rows = tenancies.map((t, i) => ({ t, outstanding: ledgers[i]?.closingBalance ?? 0 }));
    const overview = propertyId ? await api.overview(propertyId).catch(() => null) : null;
    return { rows, overview };
  }, [propertyId]);

  const period = data?.overview?.period ?? currentPeriod();

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
        <div>
          <h2 style={{ fontFamily: SERIF, fontSize: 22, margin: '0 0 3px', fontWeight: 600 }}>Ledger</h2>
          <div style={{ color: C.muted, fontSize: 13.5 }}>
            {propertyName ?? 'Property'}
            {data ? ` · ${data.rows.length} tenancies` : ''}
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: C.card, border: `1px solid ${C.line}`, borderRadius: 9, padding: '7px 12px', fontWeight: 600, fontSize: 13.5, color: C.ink }}>
          <Icon n="cal" size={15} /> {fmtPeriod(period)}
        </div>
      </div>

      {loading ? (
        <Loading label="Loading ledger…" minHeight={380} />
      ) : error || !data ? (
        <Empty icon="ledger" title="Ledger unavailable" hint="We couldn't reach the ledger. The API may be starting up." minHeight={380}>
          <button onClick={reload} style={retryBtn}>Retry</button>
        </Empty>
      ) : (
        <>
          {/* totals */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 12 }}>
            <Total k="Expected" icon="rupee" paise={data.overview?.stats?.expected} />
            <Total k="Collected" icon="check" paise={data.overview?.stats?.collected} tone="hi" />
            <Total k="Overdue" icon="bell" paise={data.overview?.stats?.overdue} tone="warn" count={data.overview?.stats?.overdueCount} />
          </div>

          {data.rows.length === 0 ? (
            <Empty icon="key" title="No tenancies yet" hint="Tenancies you add will appear here with their ledger." minHeight={220} />
          ) : (
            <div style={{ background: C.card, border: `1px solid ${C.line}`, borderRadius: 13, overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13.3, minWidth: 520 }}>
                <thead>
                  <tr>
                    {['Unit · Tenant', 'Rent', 'This month', 'Outstanding', ''].map((h, i) => (
                      <th key={h || i} style={{ background: C.bg, fontSize: 10.6, textTransform: 'uppercase', letterSpacing: '.05em', color: C.muted, fontWeight: 600, padding: '10px 14px', textAlign: i === 0 ? 'left' : i === 4 ? 'center' : 'right', whiteSpace: 'nowrap' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {data.rows.map(({ t, outstanding }) => {
                    const p = rowPill(t.status, t.dueInDays);
                    return (
                      <tr key={t.id} onClick={() => onOpen(t.id)} style={{ cursor: 'pointer', borderTop: `1px solid ${C.line2}` }}>
                        <td style={{ padding: '12px 14px', textAlign: 'left' }}>
                          <b style={{ color: C.ink, fontWeight: 600, fontSize: 13.7, display: 'block' }}>{t.unitLabel} · {t.tenantName}</b>
                          <small style={{ color: C.muted, fontSize: 12 }}>{unitDesc(t) || '–'}</small>
                        </td>
                        <td style={{ padding: '12px 14px', textAlign: 'right', ...mono({ color: C.ink }) }}>{inr(t.rentAmount)}</td>
                        <td style={{ padding: '12px 14px', textAlign: 'right' }}><Pill kind={p.kind}>{p.label}</Pill></td>
                        <td style={{ padding: '12px 14px', textAlign: 'right', ...mono({ color: outstanding > 0 ? C.red : C.hi, fontWeight: 600 }) }}>{outstanding > 0 ? inr(outstanding) : 'Clear'}</td>
                        <td style={{ padding: '12px 14px', textAlign: 'center' }}><Icon n="chev" size={15} color={C.muted} /></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function Total({ k, icon, paise, tone, count }: { k: string; icon: string; paise?: number; tone?: 'hi' | 'warn'; count?: number }) {
  const warn = tone === 'warn' && (paise ?? 0) > 0;
  const v = inrShort(paise);
  const color = warn ? C.red : tone === 'hi' ? C.hi : C.ink;
  return (
    <div style={{ background: warn ? C.redSoft : C.card, border: `1px solid ${warn ? '#ecccc1' : C.line}`, borderRadius: 12, padding: '12px 14px' }}>
      <div style={{ fontSize: 11, color: warn ? C.red : C.muted, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.04em', display: 'flex', alignItems: 'center', gap: 6 }}>
        <Icon n={icon} size={14} /> {k}
      </div>
      <div style={{ ...mono(), fontSize: 20, fontWeight: 700, color, marginTop: 6, letterSpacing: '-.02em' }}>
        {v.v}<small style={{ fontSize: 11, color: C.muted, fontWeight: 500 }}>{v.unit}{count ? ` · ${count}` : ''}</small>
      </div>
    </div>
  );
}

const retryBtn: React.CSSProperties = {
  marginTop: 4,
  border: `1px solid ${C.line}`,
  background: C.card,
  color: C.accent,
  fontWeight: 600,
  fontSize: 13,
  padding: '8px 16px',
  borderRadius: 9,
  cursor: 'pointer',
};
